if (!customElements.get('custom-product-gallery')) {
  customElements.define(
    'custom-product-gallery',
    class CustomProductGallery extends HTMLElement {
      constructor() {
        super();
        this.currentIndex = 0;
      }

      connectedCallback() {
        this.track = this.querySelector('[data-gallery-track]');
        this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
        this.prevButton = this.querySelector('[data-gallery-prev]');
        this.nextButton = this.querySelector('[data-gallery-next]');
        this.counterCurrent = this.querySelector('[data-gallery-counter-current]');
        this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
        this.thumbTrack = this.querySelector('[data-gallery-thumbs]');
        this.thumbPrevButton = this.querySelector('[data-gallery-thumb-prev]');
        this.thumbNextButton = this.querySelector('[data-gallery-thumb-next]');

        if (this.slides.length < 2) return;

        this.prevButton?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
        this.nextButton?.addEventListener('click', () => this.goTo(this.currentIndex + 1));

        this.thumbs.forEach((thumb, index) => {
          thumb.addEventListener('click', () => this.goTo(index));
        });

        this.thumbPrevButton?.addEventListener('click', () => {
          this.thumbTrack.scrollBy({ left: -240, behavior: 'smooth' });
        });
        this.thumbNextButton?.addEventListener('click', () => {
          this.thumbTrack.scrollBy({ left: 240, behavior: 'smooth' });
        });

        this.updateThumbNavVisibility();
        this.resizeObserver = new ResizeObserver(() => this.updateThumbNavVisibility());
        this.resizeObserver.observe(this.thumbTrack);

        this.trackResizeObserver = new ResizeObserver(() => this.handleTrackResize());
        this.trackResizeObserver.observe(this.track);

        this.track.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
        this.track.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
        this.track.addEventListener('touchend', this.onTouchEnd.bind(this));
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        this.trackResizeObserver?.disconnect();
      }

      goTo(index) {
        const total = this.slides.length;
        this.currentIndex = (index + total) % total;

        this.applyTransform();

        if (this.counterCurrent) this.counterCurrent.textContent = this.currentIndex + 1;

        this.thumbs.forEach((thumb, i) => {
          thumb.classList.toggle('is-active', i === this.currentIndex);
        });

        const activeThumb = this.thumbs[this.currentIndex];
        if (activeThumb) {
          activeThumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      }

      applyTransform() {
        // Pixel offset (not %), computed from the same fractional width the
        // browser uses to lay out each flex slide — explicitly reusing that
        // exact value (no rounding: some layouts land on a genuine half
        // pixel, e.g. 587.5px, and rounding it would introduce the same kind
        // of drift this is meant to eliminate) keeps the translate distance
        // bit-identical to the slide boundary instead of two independent
        // calculations that can drift apart during the transition.
        const slideWidth = this.track.getBoundingClientRect().width;
        const offset = slideWidth * this.currentIndex;
        this.track.style.transform = `translate3d(-${offset}px, 0, 0)`;
      }

      handleTrackResize() {
        if (this.slides.length < 2) return;
        const previousTransition = this.track.style.transition;
        this.track.style.transition = 'none';
        this.applyTransform();
        this.track.getBoundingClientRect(); // force reflow before re-enabling the transition
        this.track.style.transition = previousTransition;
      }

      updateThumbNavVisibility() {
        if (!this.thumbTrack) return;
        const hasOverflow = this.thumbTrack.scrollWidth > this.thumbTrack.clientWidth + 1;
        this.classList.toggle('custom-product-gallery--has-thumb-overflow', hasOverflow);
      }

      onTouchStart(event) {
        const touch = event.touches[0];
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
        this.isHorizontalSwipe = null;
      }

      onTouchMove(event) {
        if (this.touchStartX === undefined) return;
        const touch = event.touches[0];
        const deltaX = touch.clientX - this.touchStartX;
        const deltaY = touch.clientY - this.touchStartY;

        if (this.isHorizontalSwipe === null && (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)) {
          this.isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
        }

        if (this.isHorizontalSwipe) {
          event.preventDefault();
        }
      }

      onTouchEnd(event) {
        if (this.touchStartX === undefined || !this.isHorizontalSwipe) {
          this.touchStartX = undefined;
          this.isHorizontalSwipe = null;
          return;
        }

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.touchStartX;
        const threshold = 50;

        if (Math.abs(deltaX) >= threshold) {
          this.goTo(deltaX < 0 ? this.currentIndex + 1 : this.currentIndex - 1);
        }

        this.touchStartX = undefined;
        this.isHorizontalSwipe = null;
      }
    }
  );
}
